'use client';

import { forwardRef, type HTMLAttributes } from 'react';
import { Caption } from '@paypoteam/ui-components';
import { usePathname } from 'next/navigation';
import { useTheme } from 'next-themes';

import { cn } from '@/lib/utils';
import { useCssDimensions } from '@/utils/hooks/useCssDimensions';

import { ImageLink } from '@/components/atoms/ImageLink';
import PromoCodesModal from '@/components/organisms/Footer/components/PromoCodesModal';
import type { FooterConfigType } from '@/components/organisms/Footer/Footer.types';

import FooterDesktop from './components/desktop/FooterDesktop';
import FooterMobile from './components/mobile/FooterMobile';

import { useFooter } from '@/static/content/pl/footer';
import { FOOTER_CONFIG_RO } from '@/static/content/ro/footer';

import { env } from '@/env.mjs';

export interface FooterProps extends HTMLAttributes<HTMLDivElement> {}

const Footer = forwardRef<HTMLDivElement, FooterProps>(
    ({ className, ...props }, ref) => {
        const config: FooterConfigType =
            env.NEXT_PUBLIC_APP_COUNTRY === 'RO'
                ? FOOTER_CONFIG_RO
                : useFooter();
        const location = usePathname();

        const { footerInfo, footerInfoLinks, promoCodesModal, ...configProps } =
            config;

        const { forcedTheme, theme } = useTheme();
        const { minWidthLg } = useCssDimensions();

        return (
            <footer
                className={cn('w-full px-6 md:px-8', className)}
                ref={ref}
                {...props}
            >
                {minWidthLg ? (
                    <FooterDesktop
                        theme={forcedTheme || theme}
                        location={location}
                        className="hidden lg:block lg:max-w-[1168px]"
                        {...configProps}
                    />
                ) : (
                    <FooterMobile
                        theme={forcedTheme || theme}
                        location={location}
                        className="lg:hidden"
                        {...configProps}
                    />
                )}

                {promoCodesModal?.isVisible && (
                    <PromoCodesModal {...promoCodesModal} />
                )}

                <div className="mx-auto my-0 mb-8 flex flex-col-reverse gap-4 md:flex-row lg:max-w-[1168px]">
                    {footerInfo.map((elem, i) => (
                        <Caption
                            key={'caption-' + i}
                            size="3xs"
                            className="w-fit grow text-typo-secondary"
                        >
                            {elem}
                        </Caption>
                    ))}

                    {!!footerInfoLinks?.length && (
                        <div className="flex shrink-0 flex-col gap-2 lg:flex-row">
                            {footerInfoLinks.map(imageLinkProps => (
                                <ImageLink
                                    {...imageLinkProps}
                                    key={imageLinkProps.src}
                                    className="h-fit w-fit shrink-0"
                                />
                            ))}
                        </div>
                    )}
                </div>
            </footer>
        );
    },
);

export default Footer;
